import { useCallback, useEffect, useMemo, useState } from "react";

import {
    getIikoExternalMenus,
    getIikoWebhook,
    getIikoIntegration,
    getIikoOrganizations,
    getIikoTerminalGroups,
} from "services/menu/rest";
import {
    IikoCatalogSourceType,
    IikoIntegrationDto,
    IikoReferenceItemDto,
    IikoTerminalDto,
    IikoWebhookDto,
} from "services/menu/restModels";

import { ConnectIikoWebhookRequestDto } from "services/menu/restRequests";

import { useMenuScope } from "shared/context/MenuScopeContext";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuSettings } from "shared/hooks/useMenuSettings";
import { formatDateTime } from "shared/lib/dateTime";
import { localizedText } from "shared/lib/localizedMap";
import { pluralize } from "shared/lib/plural";
import {
    SelectOption,
    useNotificationSnackbar,
    UseNotificationSnackbarReturn,
} from "shared/ui";

import {
    IikoIntegrationMutations,
    useIikoIntegrationMutations,
} from "features/IikoIntegration/hooks/useIikoIntegrationMutations";
import {
    IikoIntegrationDialog,
    IikoIntegrationSaving,
    IikoIntegrationStatusTone,
    IikoIntegrationStep,
} from "features/IikoIntegration/types";

export interface IikoIntegrationState {
    menuId: number;
    menuName: string;

    loading: boolean;
    error: boolean;
    reload: () => void;

    step: IikoIntegrationStep;
    isConnected: boolean;
    // Степпер прячем только когда мастер действительно пройден: пока синхронизации не было,
    // четвёртый шаг — обычный текущий шаг, а не сводка.
    showStepper: boolean;
    statusTone: IikoIntegrationStatusTone;
    statusTitle: string;
    statusSubtitle: string;

    apiLogin: string | null;
    apiLoginDraft: string;
    setApiLoginDraft: (value: string) => void;
    isApiLoginEditing: boolean;
    startApiLoginEditing: () => void;
    cancelApiLoginEditing: () => void;
    submitApiLogin: () => void;

    organizationId: string | null;
    organizationName: string;
    organizationOptions: SelectOption[];
    isOrganizationEditing: boolean;
    startOrganizationEditing: () => void;
    selectOrganization: (iikoOrganizationId: string) => void;

    isEnabled: boolean;
    toggleEnabled: (isEnabled: boolean) => void;

    isPriceSyncEnabled: boolean;
    togglePriceSync: (isEnabled: boolean) => void;

    catalogSource: IikoCatalogSourceType;
    selectCatalogSource: (source: IikoCatalogSourceType) => void;
    externalMenuId: string | null;
    externalMenuName: string;
    externalMenuOptions: SelectOption[];
    selectExternalMenu: (externalMenuId: string) => void;
    priceCategoryId: string | null;
    // У заведения может не быть ни одного типа цены — тогда выбирать нечего и селект лишний.
    hasPriceCategories: boolean;
    priceCategoryOptions: SelectOption[];
    selectPriceCategory: (priceCategoryId: string) => void;

    terminals: IikoTerminalDto[];
    terminalName: (terminalGroupId: string) => string;
    terminalOptions: SelectOption[];
    selectedTerminalId: string;
    setSelectedTerminalId: (terminalGroupId: string) => void;
    submitTerminal: () => void;

    dialog: IikoIntegrationDialog | null;
    requestRemoveTerminal: (terminal: IikoTerminalDto) => void;
    closeDialog: () => void;
    confirmRemoveTerminal: () => void;

    lastSyncLabel: string;
    runSync: () => void;

    webhook: IikoWebhookDto | null;
    webhookChecked: boolean;
    webhookEvents: ConnectIikoWebhookRequestDto;
    // Тумблеры разошлись с тем, что реально прописано в iiko.
    webhookEventsDirty: boolean;
    connectWebhook: () => void;
    toggleWebhookEvent: (event: IikoWebhookEvent, value: boolean) => void;

    saving: IikoIntegrationSaving;
    notify: UseNotificationSnackbarReturn;
}

const TERMINAL_PLACEHOLDER = "Выберите терминал";

export type IikoWebhookEvent = keyof ConnectIikoWebhookRequestDto;

// Что применим при подключении, пока подписки в iiko нет и брать значения неоткуда.
const DEFAULT_WEBHOOK_EVENTS: ConnectIikoWebhookRequestDto = {
    stopLists: true,
    tableOrders: true,
    deliveryOrders: true,
};

/*
* Источник меню — третий шаг, но он не блокирует: у новой интеграции это номенклатура, то есть
* уже валидный выбор. Незавершённым он бывает только у внешнего меню без выбранного меню —
* такое состояние бэкенд сохранить не даст, но у старых интеграций встретиться может.
* */
const step = (integration: IikoIntegrationDto | null): IikoIntegrationStep => {
    if (!integration?.apiLogin) return 1;
    if (!integration.iikoOrganizationId) return 2;
    if (integration.catalogSource === "ExternalMenu" && !integration.externalMenuId) return 3;
    if (integration.terminals.length === 0) return 4;

    return 5;
};

export const useIikoIntegrationState = (): IikoIntegrationState => {
    const { menuId } = useMenuScope();
    const locale = useLocale();
    const notify = useNotificationSnackbar();

    const [integration, setIntegration] = useState<IikoIntegrationDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const [organizations, setOrganizations] = useState<IikoReferenceItemDto[]>([]);
    const [terminalGroups, setTerminalGroups] = useState<IikoReferenceItemDto[]>([]);
    const [webhook, setWebhook] = useState<IikoWebhookDto | null>(null);
    const [webhookEvents, setWebhookEvents] =
        useState<ConnectIikoWebhookRequestDto>(DEFAULT_WEBHOOK_EVENTS);
    // Отдельно от самого состояния: null значит «ещё не спрашивали», а не «не подключено».
    const [webhookChecked, setWebhookChecked] = useState<boolean>(false);
    const [externalMenus, setExternalMenus] = useState<IikoReferenceItemDto[]>([]);
    const [priceCategories, setPriceCategories] = useState<IikoReferenceItemDto[]>([]);

    const [apiLoginDraft, setApiLoginDraft] = useState<string>("");
    const [isApiLoginEditing, setIsApiLoginEditing] = useState<boolean>(false);
    const [isOrganizationEditing, setIsOrganizationEditing] = useState<boolean>(false);
    const [selectedTerminalId, setSelectedTerminalId] = useState<string>("");
    // Пользователь выбрал «внешнее меню», но меню ещё не назвал — сохранять нечего, а селекты
    // показать надо. Как только меню выбрано, источник приезжает из интеграции.
    const [sourceDraft, setSourceDraft] = useState<IikoCatalogSourceType | null>(null);
    const [dialog, setDialog] = useState<IikoIntegrationDialog | null>(null);

    const mutations: IikoIntegrationMutations = useIikoIntegrationMutations(menuId, notify);

    // В зависимостях эффектов — сам showError, а не notify: хук отдаёт новый объект на каждый
    // рендер, и справочники перезагружались бы бесконечно.
    const { showError } = notify;

    const { settings } = useMenuSettings(menuId);
    const menuName = settings ? localizedText(settings.name, locale) : "";

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    const apply = useCallback((result: IikoIntegrationDto | null) => {
        if (!result) return false;

        setIntegration(result);
        setApiLoginDraft(result.apiLogin ?? "");

        return true;
    }, []);

    useEffect(() => {
        // ignore-флаг: поздний ответ по прошлому меню не перезапишет свежий.
        let ignore = false;

        setLoading(true);
        setError(false);

        getIikoIntegration(menuId)
            .then((result) => {
                if (ignore) return;

                if (result) {
                    setIntegration(result);
                    setApiLoginDraft(result.apiLogin ?? "");
                } else {
                    setError(true);
                }
            })
            .catch(() => {
                if (ignore) return;

                setError(true);
            })
            .finally(() => {
                if (ignore) return;

                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, reloadKey]);

    const apiLogin = integration?.apiLogin ?? null;
    const organizationId = integration?.iikoOrganizationId ?? null;

    /*
    * Справочники подтягиваются от того, что уже сохранено: список организаций — как только есть
    * ключ, список терминалов — как только выбрана организация. Смена ключа меняет зависимость,
    * и организации перезагружаются сами — отдельных вызовов после сохранения не нужно.
    * */
    useEffect(() => {
        let ignore = false;

        if (!apiLogin) {
            setOrganizations([]);

            return;
        }

        getIikoOrganizations(menuId)
            .then((result) => {
                if (ignore) return;

                setOrganizations(result ?? []);
            })
            .catch((requestError) => {
                if (ignore) return;

                setOrganizations([]);
                showError("Не удалось загрузить список организаций из iiko", requestError);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, apiLogin, showError]);

    useEffect(() => {
        let ignore = false;

        if (!apiLogin) {
            setExternalMenus([]);
            setPriceCategories([]);

            return;
        }

        // Ручка привязана к самому ключу, а не к организации, поэтому зависимость только от него.
        getIikoExternalMenus(menuId)
            .then((result) => {
                if (ignore) return;

                setExternalMenus(result?.menus ?? []);
                setPriceCategories(result?.priceCategories ?? []);
            })
            .catch((requestError) => {
                if (ignore) return;

                setExternalMenus([]);
                setPriceCategories([]);
                showError("Не удалось загрузить список внешних меню из iiko", requestError);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, apiLogin, showError]);

    useEffect(() => {
        let ignore = false;

        if (!organizationId) {
            setTerminalGroups([]);

            return;
        }

        getIikoTerminalGroups(menuId)
            .then((result) => {
                if (ignore) return;

                setTerminalGroups(result ?? []);
            })
            .catch((requestError) => {
                if (ignore) return;

                setTerminalGroups([]);
                showError("Не удалось загрузить список терминалов из iiko", requestError);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, organizationId, showError]);

    /*
    * Подписку не храним у себя — что в фильтре iiko, то и показываем. Поэтому тумблеры
    * подтягиваем из ответа, но только у подключённого вебхука: у чужого адреса фильтр чужой,
    * и принимать его за свой нельзя.
    * */
    const applyWebhook = useCallback((result: IikoWebhookDto | null) => {
        setWebhook(result);

        if (result?.isConnected) {
            setWebhookEvents({
                stopLists: result.stopLists,
                tableOrders: result.tableOrders,
                deliveryOrders: result.deliveryOrders,
            });
        }
    }, []);

    /*
    * Статус уведомлений спрашиваем отдельно от интеграции: это поход в iiko, и вешать его на
    * каждую отрисовку экрана незачем — как со списками организаций и терминалов.
    * */
    useEffect(() => {
        let ignore = false;

        if (!organizationId) {
            setWebhook(null);
            setWebhookChecked(false);

            return;
        }

        getIikoWebhook(menuId)
            .then((result) => {
                if (ignore) return;

                applyWebhook(result);
                setWebhookChecked(true);
            })
            .catch(() => {
                if (ignore) return;

                setWebhook(null);
                setWebhookChecked(true);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, organizationId, applyWebhook]);

    const connectWebhook = useCallback(() => {
        mutations.connectWebhook(webhookEvents).then((result) => {
            if (!result) return;

            applyWebhook(result);
            setWebhookChecked(true);
        });
    }, [mutations, webhookEvents, applyWebhook]);

    /*
    * Тумблер только правит черновик, запрос уходит кнопкой. update_settings у iiko под
    * лимитом частоты, а переключают обычно несколько подряд — авто-применение упиралось
    * в TOO_MANY_REQUESTS.
    * */
    const toggleWebhookEvent = useCallback(
        (event: IikoWebhookEvent, value: boolean) => {
            setWebhookEvents((current) => ({ ...current, [event]: value }));
        },
        []
    );

    const webhookEventsDirty =
        webhook?.isConnected === true &&
        (webhook.stopLists !== webhookEvents.stopLists ||
            webhook.tableOrders !== webhookEvents.tableOrders ||
            webhook.deliveryOrders !== webhookEvents.deliveryOrders);

    const terminals = useMemo(() => integration?.terminals ?? [], [integration]);

    const currentStep = step(integration);
    const isConnected = currentStep === 5;
    const lastDataSyncAt = integration?.lastDataSyncAt ?? null;

    const organizationName = useMemo(() => {
        if (!organizationId) return "";

        return organizations.find((item) => item.id === organizationId)?.name ?? organizationId;
    }, [organizations, organizationId]);

    const catalogSource = integration?.catalogSource ?? "Nomenclature";
    const externalMenuId = integration?.externalMenuId ?? null;
    const priceCategoryId = integration?.priceCategoryId ?? null;

    const externalMenuName = useMemo(() => {
        if (!externalMenuId) return "";

        return externalMenus.find((item) => item.id === externalMenuId)?.name ?? externalMenuId;
    }, [externalMenus, externalMenuId]);

    const terminalName = useCallback(
        (terminalGroupId: string) =>
            terminalGroups.find((item) => item.id === terminalGroupId)?.name ?? terminalGroupId,
        [terminalGroups]
    );

    const organizationOptions = useMemo<SelectOption[]>(
        () => [
            {
                value: "",
                label: organizations.length ? "Выберите организацию" : "Организации не найдены",
            },
            ...organizations.map((item) => ({ value: item.id, label: item.name })),
        ],
        [organizations]
    );

    const externalMenuOptions = useMemo<SelectOption[]>(
        () => [
            {
                value: "",
                label: externalMenus.length ? "Выберите внешнее меню" : "Внешние меню не найдены",
            },
            ...externalMenus.map((item) => ({ value: item.id, label: item.name })),
        ],
        [externalMenus]
    );

    // Тип цены необязателен: если он в iiko один, выбирать нечего и меню отдаётся по нему же.
    const priceCategoryOptions = useMemo<SelectOption[]>(
        () => [
            { value: "", label: "По умолчанию" },
            ...priceCategories.map((item) => ({ value: item.id, label: item.name })),
        ],
        [priceCategories]
    );

    // В селекте только то, чего ещё нет в меню: добавлять уже добавленное нечем.
    const terminalOptions = useMemo<SelectOption[]>(() => {
        const used = new Set(terminals.map((terminal) => terminal.terminalGroupId));

        return [
            { value: "", label: TERMINAL_PLACEHOLDER },
            ...terminalGroups
                .filter((item) => !used.has(item.id))
                .map((item) => ({ value: item.id, label: item.name })),
        ];
    }, [terminalGroups, terminals]);

    const statusTone: IikoIntegrationStatusTone =
        currentStep === 5 ? "connected" : currentStep === 1 ? "idle" : "progress";

    const statusTitle = useMemo(() => {
        if (currentStep === 5) return "Подключено";
        if (currentStep === 1) return "Не подключено";

        const left = 4 - currentStep;

        return `Не подключено — ${
            left === 1 ? "остался" : "осталось"
        } ${left} ${pluralize(left, "шаг", "шага", "шагов")}`;
    }, [currentStep]);

    const statusSubtitle = useMemo(() => {
        if (currentStep === 1) {
            return "Начните с ключа Iiko Transport — он в личном кабинете iiko.";
        }

        if (currentStep === 2) return "Ключ сохранён. Выберите организацию iiko.";

        if (currentStep === 3) {
            return "Ключ и организация сохранены. Добавьте хотя бы один терминал, чтобы заказы начали уходить в iiko.";
        }

        const parts = [
            organizationName,
            `${terminals.length} ${pluralize(terminals.length, "терминал", "терминала", "терминалов")}`,
        ];

        if (lastDataSyncAt) parts.push(`синхронизировано ${formatDateTime(lastDataSyncAt)}`);

        return parts.filter(Boolean).join(" · ");
    }, [currentStep, organizationName, terminals.length, lastDataSyncAt]);

    const lastSyncLabel = lastDataSyncAt
        ? `Последняя синхронизация: ${formatDateTime(lastDataSyncAt)}`
        : "Синхронизация ещё не выполнялась";

    const startApiLoginEditing = useCallback(() => setIsApiLoginEditing(true), []);

    // Без отмены пройденный шаг, открытый по ошибке, не свернуть обратно: сохранение ключа
    // дёргает iiko и сбрасывает токен, и «сохраните тот же ключ» — плохой способ выйти.
    const cancelApiLoginEditing = useCallback(() => {
        setApiLoginDraft(apiLogin ?? "");
        setIsApiLoginEditing(false);
    }, [apiLogin]);

    const submitApiLogin = useCallback(() => {
        const value = apiLoginDraft.trim();

        if (!value) return;

        mutations.saveApiLogin(value).then((result) => {
            if (apply(result)) setIsApiLoginEditing(false);
        });
    }, [apiLoginDraft, mutations, apply]);

    const startOrganizationEditing = useCallback(() => setIsOrganizationEditing(true), []);

    const selectOrganization = useCallback(
        (value: string) => {
            if (!value) return;

            // Выбор той же организации — это «оставить как есть»: сворачиваем шаг без запроса.
            if (value === organizationId) {
                setIsOrganizationEditing(false);

                return;
            }

            mutations.saveOrganization(value).then((result) => {
                if (apply(result)) setIsOrganizationEditing(false);
            });
        },
        [mutations, apply, organizationId]
    );

    const toggleEnabled = useCallback(
        (value: boolean) => {
            mutations.saveEnabled(value).then(apply);
        },
        [mutations, apply]
    );

    /*
    * Сохранённый источник приезжает из интеграции, и черновик после этого только мешал бы:
    * без сброса возврат на номенклатуру оставлял бы подсвеченным «внешнее меню».
    * */
    const applySource = useCallback(
        (result: IikoIntegrationDto | null) => {
            if (!apply(result)) return;

            setSourceDraft(null);
        },
        [apply]
    );

    /*
    * Номенклатура сохраняется сразу — выбор полный. Внешнее меню без выбранного меню сохранить
    * нельзя, поэтому переключатель только открывает селекты, а запрос уходит с выбором меню.
    * */
    const selectCatalogSource = useCallback(
        (source: IikoCatalogSourceType) => {
            if (source === "Nomenclature") {
                mutations.saveCatalogSource("Nomenclature", null, null).then(applySource);

                return;
            }

            if (externalMenuId) {
                mutations.saveCatalogSource("ExternalMenu", externalMenuId, priceCategoryId).then(applySource);

                return;
            }

            setSourceDraft("ExternalMenu");
        },
        [mutations, applySource, externalMenuId, priceCategoryId]
    );

    const selectExternalMenu = useCallback(
        (value: string) => {
            mutations.saveCatalogSource("ExternalMenu", value, priceCategoryId).then(applySource);
        },
        [mutations, applySource, priceCategoryId]
    );

    const selectPriceCategory = useCallback(
        (value: string) => {
            if (!externalMenuId) return;

            mutations.saveCatalogSource("ExternalMenu", externalMenuId, value || null).then(applySource);
        },
        [mutations, applySource, externalMenuId]
    );

    const togglePriceSync = useCallback(
        (value: boolean) => {
            mutations.savePriceSyncEnabled(value).then(apply);
        },
        [mutations, apply]
    );

    const submitTerminal = useCallback(() => {
        if (!selectedTerminalId) return;

        mutations.addTerminal(selectedTerminalId).then((result) => {
            if (apply(result)) setSelectedTerminalId("");
        });
    }, [selectedTerminalId, mutations, apply]);

    const requestRemoveTerminal = useCallback(
        (terminal: IikoTerminalDto) => setDialog({ kind: "removeTerminal", terminal }),
        []
    );

    const closeDialog = useCallback(() => setDialog(null), []);

    const confirmRemoveTerminal = useCallback(() => {
        if (!dialog) return;

        mutations.removeTerminal(dialog.terminal.terminalGroupId).then((result) => {
            if (apply(result)) setDialog(null);
        });
    }, [dialog, mutations, apply]);

    const runSync = useCallback(() => {
        mutations.sync().then(apply);
    }, [mutations, apply]);

    return {
        menuId,
        menuName,

        loading,
        error,
        reload,

        step: currentStep,
        isConnected,
        showStepper: !(isConnected && lastDataSyncAt !== null),
        statusTone,
        statusTitle,
        statusSubtitle,

        apiLogin,
        apiLoginDraft,
        setApiLoginDraft,
        isApiLoginEditing,
        startApiLoginEditing,
        cancelApiLoginEditing,
        submitApiLogin,

        organizationId,
        organizationName,
        organizationOptions,
        isOrganizationEditing,
        startOrganizationEditing,
        selectOrganization,

        isEnabled: integration?.isEnabled ?? false,
        toggleEnabled,

        // До загрузки интеграции считаем включённым: это значение получит новая интеграция,
        // и тумблер не мигает выключенным на первом рендере.
        isPriceSyncEnabled: integration?.isPriceSyncEnabled ?? true,
        togglePriceSync,

        catalogSource: sourceDraft ?? catalogSource,
        selectCatalogSource,
        externalMenuId,
        externalMenuName,
        externalMenuOptions,
        selectExternalMenu,
        priceCategoryId,
        hasPriceCategories: priceCategories.length > 0,
        priceCategoryOptions,
        selectPriceCategory,

        terminals,
        terminalName,
        terminalOptions,
        selectedTerminalId,
        setSelectedTerminalId,
        submitTerminal,

        dialog,
        requestRemoveTerminal,
        closeDialog,
        confirmRemoveTerminal,

        lastSyncLabel,
        runSync,

        webhook,
        webhookChecked,
        webhookEvents,
        webhookEventsDirty,
        connectWebhook,
        toggleWebhookEvent,

        saving: mutations.saving,
        notify,
    };
};
